import React, { useState, useEffect, useRef } from 'react';
import { getManual } from '../services/manualsService';

// Inline manual keys here
const MANUALS: string[] = ['XDOCK', 'IN02', 'CAMS'];

// Define the shape of a command
interface Command {
  name: string;
  action: () => void;
}

// --- Define your commands here ---
const commands: Command[] = MANUALS.map((key) => ({
  name: `${key} Manual`,
  action: () => getManual(key),
}));

// Define the props for our component
interface CommandPaletteProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, setIsOpen }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredCommands = query
    ? commands.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
    : commands;

  // Effect to focus input when palette opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Effect to handle Escape key to close the palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsOpen]);

  const executeCommand = (command: Command) => {
    command.action();
    setQuery('');
    setIsOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div id="command-palette-overlay" onClick={() => setIsOpen(false)}>
      <div id="command-palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          id="command-input"
          placeholder="search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ul id="command-list" style={{padding: 0, margin: 0}}>
          {filteredCommands.length > 0 ? (
            filteredCommands.map((command, idx) => {
              // Choose an icon based on command name
              let icon = '📄';
              if (command.name.toLowerCase().includes('manual')) icon = '📘';
              //if (command.name.toLowerCase().includes('history')) icon = '🕑';
              //if (command.name.toLowerCase().includes('logout')) icon = '🚪';

              return (
                <React.Fragment key={command.name}>
                  <li
                    onMouseDown={() => executeCommand(command)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      margin: '0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.92em',
                      fontFamily: 'Segoe UI, Roboto, Arial, sans-serif',
                      background: 'rgba(245, 250, 255, 0.95)',
                      boxShadow: '0 1px 4px rgba(0,81,161,0.04)',
                      border: '1px solid #e3eaf3',
                      transition: 'background 0.18s, box-shadow 0.18s, transform 0.18s',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseOver={e => {
                      (e.currentTarget as HTMLLIElement).style.background = '#e6f0fa';
                      (e.currentTarget as HTMLLIElement).style.boxShadow = '0 2px 8px rgba(0,81,161,0.08)';
                      (e.currentTarget as HTMLLIElement).style.transform = 'scale(1.01)';
                    }}
                    onMouseOut={e => {
                      (e.currentTarget as HTMLLIElement).style.background = 'rgba(245, 250, 255, 0.95)';
                      (e.currentTarget as HTMLLIElement).style.boxShadow = '0 1px 4px rgba(0,81,161,0.04)';
                      (e.currentTarget as HTMLLIElement).style.transform = 'scale(1)';
                    }}
                  >
                    <span style={{
                      fontSize: '1em',
                      background: 'linear-gradient(135deg, #00b4d8 60%, #0051a1 100%)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 2px rgba(0,81,161,0.08)',
                      flexShrink: 0
                    }}>{icon}</span>
                    <span style={{
                      fontWeight: 500,
                      letterSpacing: '0.01em',
                      color: '#1a2a3a',
                      flex: 1
                    }}>{command.name}</span>
                  </li>
                  {/* Divider except after last item */}
                  {idx < filteredCommands.length - 1 && (
                    <hr style={{
                      border: 'none',
                      borderTop: '1px solid #e3eaf3',
                      margin: '0 12px'
                    }} />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <li style={{
              cursor: 'default',
              padding: '6px 12px',
              color: '#888',
              fontSize: '0.92em',
              textAlign: 'center'
            }}>
              No results found
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default CommandPalette;